import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { FileSpreadsheet, X } from 'lucide-react';
import { BasePrimaryButton, Button, Logo, useToast } from '@practium/ui';
import styles from './ImportProductsModal.module.css';

export interface ImportProductsModalProps {
	isOpen: boolean;
	onClose: () => void;
	onImport?: (file: File) => void;
}

const VALID_EXTENSIONS = ['.xls', '.xlsx'];
const VALID_MIME_TYPES = [
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_FILE_SIZE_LABEL = '10 MB';

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateFile = (file: File): string | null => {
	const hasValidMime = VALID_MIME_TYPES.includes(file.type);

	const lastDotIndex = file.name.lastIndexOf('.');
	const ext = lastDotIndex !== -1 ? file.name.substring(lastDotIndex).toLowerCase() : '';
	const hasValidExt = VALID_EXTENSIONS.includes(ext);

	if (!hasValidMime && !hasValidExt) {
		return `Formato inválido. Envie um arquivo .xls ou .xlsx`;
	}
	if (file.size === 0) {
		return 'O arquivo está corrompido ou vazio';
	}
	if (file.size > MAX_FILE_SIZE_BYTES) {
		return `Arquivo muito grande. O limite é de ${MAX_FILE_SIZE_LABEL}`;
	}
	return null;
};

export const ImportProductsModal = ({ isOpen, onClose, onImport }: ImportProductsModalProps) => {
	const [isDragging, setIsDragging] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { error: toastError } = useToast();

	if (!isOpen) return null;

	const trySelectFile = (file: File) => {
		const validationError = validateFile(file);
		if (validationError) {
			toastError(validationError);
			return;
		}
		setSelectedFile(file);
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) trySelectFile(file);
		e.target.value = '';
	};

	const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'copy';
		setIsDragging(true);
	};

	const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files?.[0];
		if (file) trySelectFile(file);
	};

	const handleClose = () => {
		setSelectedFile(null);
		onClose();
	};

	const handleImport = () => {
		if (!selectedFile) return;
		const validationError = validateFile(selectedFile);
		if (validationError) {
			toastError(validationError);
			return;
		}
		onImport?.(selectedFile);
		handleClose();
	};

	const handleRemoveFile = () => {
		setSelectedFile(null);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const displayExt = selectedFile
		? selectedFile.name.substring(selectedFile.name.lastIndexOf('.'))
		: '';

	return (
		<div className={styles.overlay} onClick={handleClose}>
			<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
				<div className={styles.header}>
					<div className={styles.headerTop}>
						<Logo className={styles.logo} />
						<button
							type="button"
							className={styles.closeBtn}
							onClick={handleClose}
							aria-label="Fechar modal"
						>
							<CloseOutlined fontSize="inherit" />
						</button>
					</div>
					<h2 className={styles.title}>Importar Produtos</h2>
				</div>

				<div className={styles.body}>
					<input
						ref={fileInputRef}
						id="product-import-input"
						type="file"
						accept=".xls,.xlsx"
						className={styles.hiddenInput}
						onChange={handleFileChange}
					/>

					{selectedFile ? (
						<div className={styles.fileCard}>
							<div className={styles.fileCardIcon}>
								<FileSpreadsheet className={styles.fileIcon} />
							</div>
							<div className={styles.fileInfo}>
								<span className={styles.fileName}>{selectedFile.name}</span>
								<span className={styles.fileMeta}>
									{formatFileSize(selectedFile.size)} · {displayExt}
								</span>
							</div>
							<div className={styles.fileActions}>
								<button
									type="button"
									className={styles.changeFileBtn}
									onClick={() => fileInputRef.current?.click()}
								>
									Trocar
								</button>
								<button
									type="button"
									className={styles.removeFileBtn}
									onClick={handleRemoveFile}
									aria-label="Remover arquivo"
								>
									<X size={16} />
								</button>
							</div>
						</div>
					) : (
						<label
							htmlFor="product-import-input"
							className={`${styles.uploadZone} ${isDragging ? styles.dragging : ''}`}
							onDragOver={handleDragOver}
							onDragLeave={() => setIsDragging(false)}
							onDrop={handleDrop}
						>
							<FileSpreadsheet className={styles.uploadIcon} />
							<span className={styles.uploadTitle}>Arraste ou clique para fazer upload</span>
							<span className={styles.uploadSubtitle}>Formatos aceitos: .xls e .xlsx</span>
						</label>
					)}
				</div>

				<div className={styles.footer}>
					<Button variant="cancel" type="button" className={styles.actionBtn} onClick={handleClose}>
						Cancelar
					</Button>
					<BasePrimaryButton
						onClick={handleImport}
						disabled={!selectedFile}
						className={styles.actionBtn}
					>
						Importar
					</BasePrimaryButton>
				</div>
			</div>
		</div>
	);
};
